import React from "react";
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Phone,
  Mail,
  FileText,
  Globe,
  LogIn,
  Info,
  Shield,
  TrendingUp,
} from "lucide-react";
import { motion } from "framer-motion";

const ActivationRequirements = ({ promoter }) => {
  if (!promoter) return null;

  // Check activation requirements
  // If either email OR phone is verified, mark both as verified
  const emailVerifiedOriginal = !!promoter.email_verified_at;
  const phoneVerifiedOriginal = !!promoter.phone_number_verified_at;
  const eitherVerified = emailVerifiedOriginal || phoneVerifiedOriginal;
  const emailVerified = eitherVerified;
  const phoneVerified = eitherVerified;
  
  // Note: These fields may not exist in the data yet, so we'll check for them
  // const webinarCompleted = promoter?.webinar_completed || promoter?.webinar_attended || false;
  // Terms and conditions - always pass
  const termsAccepted = true;
  
  // Channel confirmed - auto-pass if at least one channel exists
  const hasChannels = promoter?.promotion_channels && Object.values(promoter.promotion_channels).some(
    channels => Array.isArray(channels) && channels.length > 0
  );
  const channelConfirmed = hasChannels;
  
  // First dashboard login - always pass for now
  const firstLogin = true;
  
  const activationRequirements = [
    {
      id: "email_verification",
      label: "Email Verification",
      description: "Email or phone must be verified",
      icon: Mail,
      completed: emailVerified,
      critical: true,
      details: emailVerified
        ? emailVerifiedOriginal
          ? `Verified on ${new Date(promoter.email_verified_at).toLocaleDateString()}`
          : "Auto-verified (phone verified)"
        : "Email not verified",
    },
    {
      id: "phone_verification",
      label: "Phone Verification",
      description: "Phone or email must be verified",
      icon: Phone,
      completed: phoneVerified,
      critical: true,
      details: phoneVerified
        ? phoneVerifiedOriginal
          ? `Verified on ${new Date(promoter.phone_number_verified_at).toLocaleDateString()}`
          : "Auto-verified (email verified)"
        : "Phone not verified",
    },
    // {
    //   id: "webinar",
    //   label: "Onboarding Webinar",
    //   description: "Webinar attended OR replay completed",
    //   icon: Video,
    //   completed: webinarCompleted,
    //   critical: true,
    //   details: webinarCompleted
    //     ? "Webinar completed"
    //     : "Webinar not completed",
    // },
    {
      id: "terms",
      label: "Publisher Terms & Conditions",
      description: "Terms & Conditions accepted",
      icon: FileText,
      completed: termsAccepted,
      critical: true,
      details: "Auto-passed - Terms accepted during registration",
    },
    {
      id: "channel_confirmed",
      label: "Channel Confirmed",
      description: "At least one promotion channel uploaded in profile",
      icon: Globe,
      completed: channelConfirmed,
      critical: true,
      details: channelConfirmed
        ? "Channel confirmed - at least one channel uploaded"
        : "No promotion channels uploaded",
    },
    {
      id: "first_login",
      label: "First Dashboard Login",
      description: "Publisher dashboard access requirement",
      icon: LogIn,
      completed: firstLogin,
      critical: true,
      details: "Auto-passed - will login after approval",
    },
  ];

  const allActivationRequirementsMet = activationRequirements.every((req) => req.completed);
  const completedCount = activationRequirements.filter((req) => req.completed).length;
  const totalCount = activationRequirements.length;

  // Determine activation status
  const getActivationStatus = () => {
    if (!promoter.affiliate_status) return "registered";
    if (promoter.affiliate_status === "rejected") return "rejected";
    if (promoter.affiliate_status === "pending") return "registered";
    if (["full_approval", "limited_approval"].includes(promoter.affiliate_status)) {
      // Only show as "activated" or "live" if is_active is true
      if (promoter.is_active) {
        return "live";
      }
      // If approved but not active, show as "approved" regardless of requirements
      return "approved";
    }
    return "registered";
  };

  const activationStatus = getActivationStatus();

  // Define status order for determining which statuses are passed
  const statusOrder = ["registered", "approved", "activated", "live", "dormant"];
  const currentStatusIndex = statusOrder.indexOf(activationStatus);

  const statusDefinitions = [
    {
      status: "registered",
      label: "Registered",
      description: "Form submitted",
      color: "gray",
      icon: FileText,
    },
    {
      status: "approved",
      label: "Approved",
      description: "Vetting completed",
      color: "blue",
      icon: CheckCircle2,
    },
    {
      status: "activated",
      label: "Activated",
      description: "Eligible to promote",
      color: "green",
      icon: Shield,
    },
    {
      status: "live",
      label: "Live",
      description: "Actively promoting campaigns",
      color: "emerald",
      icon: TrendingUp,
    },
    {
      status: "dormant",
      label: "Dormant",
      description: "No activity in 30–60 days",
      color: "amber",
      icon: AlertCircle,
    },
  ];

  const getStatusColor = (color) => {
    const colors = {
      gray: "bg-gray-100 text-gray-700 border-gray-300",
      blue: "bg-blue-100 text-blue-700 border-blue-300",
      green: "bg-green-100 text-green-700 border-green-300",
      emerald: "bg-emerald-100 text-emerald-700 border-emerald-300",
      amber: "bg-amber-100 text-amber-700 border-amber-300",
      red: "bg-red-100 text-red-700 border-red-300",
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="space-y-6">
      {/* Key Distinction Banner */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-5 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-xl border-2 border-blue-200"
      >
        <div className="flex gap-3 items-start">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Info className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="mb-2 text-lg font-bold text-blue-900">
              Approval ≠ Activation
            </h3>
            <div className="space-y-2 text-sm text-blue-800">
              <p>
                <strong>Approval</strong> confirms suitability for the platform.
              </p>
              <p>
                <strong>Activation</strong> confirms readiness to promote and earn commissions.
              </p>
              <p className="mt-3 font-semibold text-blue-900">
                Until activation is complete:
              </p>
              <ul className="ml-4 space-y-1 list-disc">
                <li>No live campaign promotion</li>
                <li>No commission eligibility</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Activation Status Definitions */}
      <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
        <h3 className="mb-4 text-lg font-bold text-gray-900">
          Activation Status Definitions
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {statusDefinitions.map((def) => {
            const Icon = def.icon;
            const defStatusIndex = statusOrder.indexOf(def.status);
            const isCurrentStatus = def.status === activationStatus;
            const isPassed = currentStatusIndex !== -1 && defStatusIndex !== -1 && defStatusIndex < currentStatusIndex;
            
            return (
              <div
                key={def.status}
                className={`p-4 rounded-lg border-2 transition-all ${
                  isCurrentStatus
                    ? `${getStatusColor(def.color)} ring-2 ring-offset-2 ${
                        def.color === "gray"
                          ? "ring-gray-400"
                          : def.color === "blue"
                          ? "ring-blue-400"
                          : def.color === "green"
                          ? "ring-green-400"
                          : def.color === "emerald"
                          ? "ring-emerald-400"
                          : "ring-amber-400"
                      }`
                    : isPassed
                    ? "bg-green-50 border-green-300"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex gap-3 items-start">
                  {isPassed ? (
                    <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-green-600" />
                  ) : (
                    <Icon
                      className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        isCurrentStatus
                          ? def.color === "gray"
                            ? "text-gray-600"
                            : def.color === "blue"
                            ? "text-blue-600"
                            : def.color === "green"
                            ? "text-green-600"
                            : def.color === "emerald"
                            ? "text-emerald-600"
                            : "text-amber-600"
                          : "text-gray-400"
                      }`}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex gap-2 items-center mb-1">
                      <h4 className={`font-semibold ${
                        isPassed ? "text-green-900" : "text-gray-900"
                      }`}>
                        {def.label}
                      </h4>
                      {isCurrentStatus && (
                        <span className="px-2 py-0.5 text-xs font-bold text-white bg-blue-600 rounded">
                          CURRENT
                        </span>
                      )}
                      {isPassed && (
                        <span className="px-2 py-0.5 text-xs font-bold text-white bg-green-600 rounded">
                          PASSED
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${
                      isPassed ? "text-green-700" : "text-gray-600"
                    }`}>
                      {def.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Activation Requirements Checklist */}
      <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex gap-3 justify-between items-start pb-4 mb-6 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Activation Requirements
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              ALL requirements must be met for activation
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-sm font-medium text-gray-600">
              {completedCount}/{totalCount}
            </span>
            {allActivationRequirementsMet ? (
              <div className="flex gap-2 items-center px-3 py-1.5 text-green-700 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm font-semibold">Ready to Activate</span>
              </div>
            ) : (
              <div className="flex gap-2 items-center px-3 py-1.5 text-amber-700 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-semibold">Pending</span>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          {activationRequirements.map((requirement) => {
            const Icon = requirement.icon;
            return (
              <div
                key={requirement.id}
                className={`p-4 rounded-lg border transition-all ${
                  requirement.completed
                    ? "bg-green-50 border-green-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <div className="flex gap-3 items-start">
                  <div
                    className={`p-2 rounded-lg ${
                      requirement.completed ? "bg-green-100" : "bg-red-100"
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${
                        requirement.completed ? "text-green-600" : "text-red-600"
                      }`}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex gap-2 items-center mb-1">
                      <h4 className="font-semibold text-gray-900">
                        {requirement.label}
                      </h4>
                      {requirement.critical && (
                        <span className="px-2 py-0.5 text-xs font-medium text-red-700 bg-red-100 rounded">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="mb-2 text-sm text-gray-600">
                      {requirement.description}
                    </p>
                    <div
                      className={`p-2 rounded text-xs font-medium ${
                        requirement.completed
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {requirement.details}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {requirement.completed ? (
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-500" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!allActivationRequirementsMet && (
          <div className="flex gap-3 items-start p-4 mt-4 bg-amber-50 rounded-lg border-2 border-amber-200">
            <AlertCircle className="flex-shrink-0 w-5 h-5 text-amber-600" />
            <div>
              <h4 className="mb-1 font-semibold text-amber-900">
                Activation Pending
              </h4>
              <p className="text-sm text-amber-800">
                This publisher cannot be activated until all {totalCount} requirements are met. 
                They will not be able to promote campaigns or earn commissions until activation is complete.
              </p>
            </div>
          </div>
        )}

        {allActivationRequirementsMet && !promoter.is_active && (
          <div className="flex gap-3 items-start p-4 mt-4 bg-green-50 rounded-lg border-2 border-green-200">
            <CheckCircle2 className="flex-shrink-0 w-5 h-5 text-green-600" />
            <div>
              <h4 className="mb-1 font-semibold text-green-900">
                Ready for Activation
              </h4>
              <p className="text-sm text-green-800">
                All activation requirements have been met. This publisher is eligible to be activated 
                and can start promoting campaigns once activated.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Post-Activation Performance Review */}
      <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex gap-3 items-start mb-4">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="flex-1">
            <h3 className="mb-2 text-lg font-bold text-gray-900">
              Post-Activation Performance Review
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Monthly monitoring and evaluation
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">
                      Metric
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">
                      Measured
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      Campaigns promoted
                    </td>
                    <td className="px-4 py-3 text-center">
                      <CheckCircle2 className="w-5 h-5 mx-auto text-green-500" />
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      Valid conversions
                    </td>
                    <td className="px-4 py-3 text-center">
                      <CheckCircle2 className="w-5 h-5 mx-auto text-green-500" />
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      Compliance record
                    </td>
                    <td className="px-4 py-3 text-center">
                      <CheckCircle2 className="w-5 h-5 mx-auto text-green-500" />
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      Audience engagement quality
                    </td>
                    <td className="px-4 py-3 text-center">
                      <CheckCircle2 className="w-5 h-5 mx-auto text-green-500" />
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      Feedback / complaints
                    </td>
                    <td className="px-4 py-3 text-center">
                      <CheckCircle2 className="w-5 h-5 mx-auto text-green-500" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
              <p className="text-xs text-indigo-800">
                <strong>Outcome:</strong> Consistent positive performance → upgrade eligibility | 
                Violations → restriction or suspension
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivationRequirements;
